/**
 * The frame both dock tables render through (round-3 spec §B3): the clearance
 * table and the station-volumes table used to be two hand-built copies of the
 * same layout that had drifted apart — different toolbars, a 28 px row, an
 * amber walk row, a totals row that did not look like totals (audit A6).
 *
 * What the frame owns, so neither table can drift again:
 *  - **One toolbar row, h-8.** Search on the left, filter toggles beside it,
 *    `Columns ▾` (a popover, replacing a run of inline checkboxes), and the
 *    table's actions (Copy, Reset order) pushed right. It never wraps — a
 *    wrapped toolbar is what left a 280 px dock room for no rows at all.
 *  - **The table chrome:** sticky header, sticky totals band
 *    (`--sv-surface`, a 2 px top rule, semibold) so a total cannot be read as
 *    one more interval, 24 px rows, figures `tabular-nums text-right`, the
 *    sort glyph at 60 % and 100 % on the sorted column.
 *  - **The row CSS** (`DOCK_TABLE_CSS`): hover and the WALK row. The walk is
 *    the dock's one neutral-blue mark — a 10 % `--sv-walk` wash (sky-500 /
 *    sky-400 dark) and a 2 px bar in `--sv-walk` on the leading edge, never
 *    amber: amber is the near-miss rung and the MARGINAL verdict, and a walked
 *    row inside a Marginal block used to read as a stronger Marginal.
 *  - **The window** (`useDockTableWindow`): fixed-height rows make the
 *    visible slice arithmetic; spacer rows keep the scrollbar honest; a hover
 *    published from the chart or the 3D view is a DOM attribute, never React
 *    state; one roving tab stop moved by the arrows.
 *
 * What it does NOT own: the columns, the cells and the sorting rules. Those
 * are each table's content (`lib/clearance/table.ts`,
 * `lib/stationVolumes/tableModel.ts`) and stay there.
 */
import { useStore } from 'jotai';
import { ChevronDown } from 'lucide-react';
import {
  type ReactNode,
  type RefObject,
  type UIEvent,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { stationVolumeHoverBinAtom } from '@/atoms/stationVolumes.atom';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { revealScrollTop } from '@/lib/stationVolumes/tableModel';
import { cn } from '@/lib/utils';

import { DOCK_CONTROL, DOCK_TEXT } from './dockStyles';

/** Every body row is exactly this tall, which is what makes the window
 *  arithmetic exact — and it is the 24 px hit-target floor (spec §5 A11y 5). */
export const DOCK_TABLE_ROW_PX = 24;
/** Rows kept rendered either side of the viewport, so a fast scroll or an
 *  arrow-key step never shows a gap. */
const OVERSCAN_ROWS = 8;
/** Used until the ResizeObserver reports — jsdom and the first paint. */
const DEFAULT_VIEWPORT_PX = 320;

/**
 * A body or totals cell: 4 px + a 16 px line + 3 px = 23 px, and the row's
 * 1 px bottom rule makes 24. Exactly 24, because the window's arithmetic
 * (spacer heights, `revealScrollTop`) assumes it: the old 28 px row measured
 * 29 in the browser, and that pixel per row compounded — a walked row 20
 * intervals down was "revealed" under the totals band.
 */
export const DOCK_TABLE_CELL = 'px-2 pt-1 pb-[3px] leading-4 whitespace-nowrap';
/** The `VerdictBadge` shrunk to the row: 11 px on a 16 px pill, so it never
 *  stretches the 24 px row (an 18 px pill made the clearance rows 27 px). */
export const DOCK_TABLE_BADGE = 'px-1.5 py-0 text-[11px] leading-4';
/** A cell that shrinks to its content instead of taking a share of the
 *  table's slack — the Station column, the verdict badge. */
export const DOCK_TABLE_SHRINK = 'w-px';

/**
 * The rules Tailwind cannot express against the `--sv-*` tokens. Hover and
 * walk live here rather than in inline styles precisely so that hovering a
 * row changes no React state. `>:first-child`, not `>td:first-child`: the
 * clearance table's station cell is a row header (`<th scope="row">`).
 *
 * The walk bar is an inset shadow on the leading cell rather than a border:
 * in a `border-collapse` table a cell border is resolved against its
 * neighbours' and the row's bottom rule, and a 2 px transparent border on
 * every row would widen the column. A shadow takes no space.
 */
export const DOCK_TABLE_CSS = `
.sv-row:hover,.sv-row[data-hover='true']{background:color-mix(in srgb, currentColor 6%, transparent);}
.sv-row[aria-current]{background:color-mix(in srgb, var(--sv-walk) 10%, transparent);}
.sv-row[aria-current]:hover,.sv-row[aria-current][data-hover='true']{background:color-mix(in srgb, var(--sv-walk) 16%, transparent);}
.sv-row[aria-current]>:first-child{box-shadow:inset 2px 0 0 var(--sv-walk);}
.dock-sort::after{content:attr(data-glyph);}
.dock-table tfoot>tr:first-child>*{box-shadow:inset 0 2px 0 color-mix(in srgb, currentColor 22%, transparent);}
@media (prefers-reduced-motion:no-preference){
  .sv-row{transition:background-color 120ms cubic-bezier(.2,0,0,1);}
}
@media (forced-colors:active){
  .sv-row[aria-current]{outline:2px solid Highlight;outline-offset:-2px;}
  .sv-row[aria-current]>:first-child{box-shadow:none;}
  .dock-table tfoot>tr:first-child>*{box-shadow:none;border-top:2px solid CanvasText;}
}
`;

// ------------------------------------------------------------------ frame

export interface DockTableFrameProps {
  /** Left of the toolbar: `DockTableSearch`, `DockTableFilter`s. */
  toolbar: ReactNode;
  /** The `DockColumnsMenu`, after the filters. */
  columns?: ReactNode;
  /** Pushed right: Reset order, Copy. */
  actions?: ReactNode;
  /** The scrolling viewport — `useDockTableWindow().scrollRef`. */
  scrollRef: RefObject<HTMLDivElement | null>;
  onScroll: (e: UIEvent<HTMLDivElement>) => void;
  /** `clearance-table-scroll` / `sv-table-scroll`. */
  scrollTestId: string;
  /** The interaction hint, as the viewport's tooltip rather than a line. */
  scrollTitle?: string;
  /** The table's `<caption>` (visually hidden). */
  caption: ReactNode;
  /** `<thead>`, `<tbody>`, `<tfoot>`. Put `DOCK_TABLE_HEAD` on the header
   *  cells and `DOCK_TABLE_FOOT` on the totals band. */
  children: ReactNode;
  testId?: string;
}

/** The sticky header cell: sits on the dock surface so rows pass under it;
 *  its bottom rule is an inset shadow (see `DOCK_TABLE_FOOT`). */
export const DOCK_TABLE_HEAD =
  'sticky top-0 z-10 bg-[var(--sv-surface)] px-2 py-0.5 font-semibold whitespace-nowrap shadow-[inset_0_-1px_0_var(--sv-grid)]';
/** The sticky totals band. The 2 px top rule is in `DOCK_TABLE_CSS` (it has to
 *  sit on the band's first row only, and a corridor row stacks under it), and
 *  it is an inset shadow for the header's reason: in a `border-collapse`
 *  table a border belongs to the table grid and does not travel with a
 *  sticky cell, so a bordered sticky band loses its rule as soon as it
 *  sticks. */
export const DOCK_TABLE_FOOT = 'sticky bottom-0 z-10 bg-[var(--sv-surface)] font-semibold';

export const DockTableFrame = ({
  toolbar,
  columns,
  actions,
  scrollRef,
  onScroll,
  scrollTestId,
  scrollTitle,
  caption,
  children,
  testId,
}: DockTableFrameProps) => (
  // `min-w-0`: the table's min-content width must not widen this column past
  // the dock — the overflow belongs to the viewport below, never the dock.
  // `@container`: the toolbar below reads the frame's own width.
  <div data-testid={testId} className='@container flex h-full min-h-0 w-full min-w-0 flex-col gap-1.5 text-xs'>
    <style>{DOCK_TABLE_CSS}</style>
    {/* One h-8 row on a desktop dock. Below 560 px (a phone) it WRAPS: the
        groups used to shrink under their own content there and paint over
        each other (Search / Only violations / Columns / Copy in one smear).
        A phone dock is portrait-tall, so a second toolbar line costs little;
        above 560 px a row that still does not fit scrolls rather than
        overlapping. */}
    <div
      data-testid={testId ? `${testId}-toolbar` : 'dock-table-toolbar'}
      className='flex h-8 min-w-0 shrink-0 items-center gap-3 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden @max-[560px]:h-auto @max-[560px]:flex-wrap @max-[560px]:gap-y-1.5 @max-[560px]:overflow-visible'
    >
      <div className='flex shrink-0 items-center gap-3 @max-[560px]:min-w-0 @max-[560px]:shrink @max-[560px]:flex-wrap @max-[560px]:gap-y-1.5'>
        {toolbar}
      </div>
      <div className='flex shrink-0 items-center'>{columns}</div>
      {actions && <div className='ml-auto flex shrink-0 items-center gap-1'>{actions}</div>}
    </div>
    {/* The table owns its scroll, so the header and totals stay put while
        the rows move under them and the dock body never scrolls sideways. */}
    <div
      ref={scrollRef}
      data-testid={scrollTestId}
      title={scrollTitle}
      onScroll={onScroll}
      className='min-h-0 flex-1 overflow-auto'
    >
      <table className='dock-table sv-num w-full border-collapse text-right tabular-nums'>
        <caption className='sr-only'>{caption}</caption>
        {children}
      </table>
    </div>
  </div>
);

// ---------------------------------------------------------------- toolbar

export interface DockTableSearchProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  clearLabel: string;
  /** The text is there but parses as no station. */
  invalid?: boolean;
  errorText?: string;
  testId?: string;
  clearTestId?: string;
}

/** Search STA: label, a mono input, a Clear once there is text, and the
 *  parse error inline (truncating — the toolbar row never wraps). */
export const DockTableSearch = ({
  label,
  value,
  onChange,
  placeholder,
  clearLabel,
  invalid,
  errorText,
  testId,
  clearTestId,
}: DockTableSearchProps) => {
  const id = useId();
  return (
    <div className='flex min-w-0 items-center gap-1.5'>
      <label htmlFor={id} className='shrink-0 text-slate-600 dark:text-stone-300'>
        {label}
      </label>
      <input
        id={id}
        data-testid={testId}
        type='search'
        inputMode='text'
        value={value}
        aria-invalid={invalid || undefined}
        aria-describedby={invalid ? `${id}-error` : undefined}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        // One clear affordance, not two: WebKit's own search ✕ would sit
        // beside the Clear button that Firefox needs.
        className={cn(
          'sv-sta h-7 w-32 shrink-0 rounded-md border border-slate-300 bg-transparent px-2 text-xs placeholder:text-slate-400 aria-invalid:border-[var(--sv-warn)] dark:border-stone-600 dark:placeholder:text-stone-500 [&::-webkit-search-cancel-button]:appearance-none',
          DOCK_CONTROL.focus,
        )}
      />
      {value.length > 0 && (
        <button
          type='button'
          data-testid={clearTestId}
          onClick={() => onChange('')}
          className={cn(
            'h-7 shrink-0 rounded-md px-1.5 text-xs text-slate-600 hover:bg-slate-200/70 dark:text-stone-300 dark:hover:bg-stone-700/70',
            DOCK_CONTROL.focus,
          )}
        >
          {clearLabel}
        </button>
      )}
      {invalid && errorText && (
        <span id={`${id}-error`} title={errorText} className='min-w-0 truncate text-[var(--sv-warn)]'>
          {errorText}
        </span>
      )}
    </div>
  );
};

export interface DockTableFilterProps {
  label: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  title?: string;
  /** On the checkbox itself (`clearance-table-only-violations`, …). */
  testId?: string;
}

/** A filter toggle in the toolbar: a checkbox and its label, one hit target. */
export const DockTableFilter = ({ label, checked, onCheckedChange, title, testId }: DockTableFilterProps) => {
  const id = useId();
  return (
    <div className='flex shrink-0 items-center gap-1.5' title={title}>
      <Checkbox id={id} data-testid={testId} checked={checked} onCheckedChange={(c) => onCheckedChange(!!c)} />
      <label htmlFor={id} className='cursor-pointer whitespace-nowrap'>
        {label}
      </label>
    </div>
  );
};

export interface DockColumnOption {
  /** Becomes the checkbox id's suffix (`…-col-<key>`). */
  key: string;
  label: string;
  /** What the column means — the same sentence its header carries. */
  title?: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  /** On the checkbox (`sv-table-col-aea`, `clearance-table-col-position`, …). */
  testId?: string;
  /** Off because the width ran out, not because the reader said so
   *  (`useFitColumnGroups`) — the row says which. */
  autoHidden?: boolean;
}

export interface DockColumnsMenuProps {
  options: DockColumnOption[];
  /** The trigger's `data-testid`; the content is `${testId}-menu`. */
  testId?: string;
  /** Defaults to `stationVolumes.table.columns`. */
  label?: string;
  /** Controlled open state (tests, the harness). */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Columns hidden to fit the width (`useFitColumnGroups().hiddenColumns`):
   *  shown on the trigger as "+N columns hidden", so a column that went
   *  missing on a narrow dock is never a silent loss. */
  hiddenCount?: number;
}

/**
 * `Columns ▾`: the optional columns as checkboxes in a popover. A Radix
 * POPOVER, not a DropdownMenu, for `DockViewMenu`'s reason — a menu item
 * closes the menu, and a reader turning two columns on would reopen it
 * between them. Each row is checkbox-then-label, so the label's `title` (the
 * column's own help sentence) is a sibling of the checkbox.
 */
export const DockColumnsMenu = ({
  options,
  testId,
  label,
  open,
  onOpenChange,
  hiddenCount = 0,
}: DockColumnsMenuProps) => {
  const { t } = useTranslation('project');
  const ids = useId();
  if (options.length === 0) return null;
  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button
          type='button'
          data-testid={testId}
          title={hiddenCount > 0 ? t('dock.columnsHiddenHelp') : undefined}
          className={DOCK_CONTROL.button}
        >
          {label ?? t('stationVolumes.table.columns')}
          {hiddenCount > 0 && (
            <span data-testid={testId ? `${testId}-hidden` : undefined} className={DOCK_TEXT.metaMuted}>
              {t('dock.columnsHidden', { count: hiddenCount, formatted: hiddenCount.toLocaleString() })}
            </span>
          )}
          <ChevronDown className={DOCK_CONTROL.iconSm} aria-hidden />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align='start'
        collisionPadding={8}
        data-testid={testId ? `${testId}-menu` : undefined}
        className='sv-palette flex w-60 flex-col gap-0.5 border-slate-200 bg-white p-1 text-xs text-slate-700 shadow-lg dark:border-stone-700 dark:bg-stone-900 dark:text-stone-200'
      >
        {options.map((o) => (
          <div
            key={o.key}
            className='flex min-h-8 items-center gap-2 rounded px-2 py-1 hover:bg-slate-100 dark:hover:bg-stone-800'
          >
            <Checkbox
              id={`${ids}-col-${o.key}`}
              data-testid={o.testId}
              checked={o.checked}
              onCheckedChange={(c) => o.onCheckedChange(!!c)}
            />
            <label htmlFor={`${ids}-col-${o.key}`} title={o.title} className='flex flex-1 cursor-pointer flex-col'>
              <span>{o.label}</span>
              {o.autoHidden && <span className={DOCK_TEXT.metaMuted}>{t('dock.columnHiddenToFit')}</span>}
            </label>
          </div>
        ))}
      </PopoverContent>
    </Popover>
  );
};

/** A plain text action in the toolbar (Reset order). */
export const DockTableAction = ({
  children,
  onClick,
  title,
  testId,
  disabled,
  outlined,
}: {
  children: ReactNode;
  onClick: () => void;
  title?: string;
  testId?: string;
  /** A DATA condition only (nothing to copy) — never a paywall. */
  disabled?: boolean;
  /** Bordered, for the table's one primary action (Copy). */
  outlined?: boolean;
}) => (
  <button
    type='button'
    data-testid={testId}
    title={title}
    disabled={disabled}
    onClick={onClick}
    className={cn(
      outlined
        ? DOCK_CONTROL.button
        : cn(
            'h-7 shrink-0 rounded-md px-2 text-xs whitespace-nowrap text-slate-600 hover:bg-slate-200/70 dark:text-stone-300 dark:hover:bg-stone-700/70',
            DOCK_CONTROL.focus,
          ),
      'disabled:pointer-events-none disabled:opacity-50',
    )}
  >
    {children}
  </button>
);

// ----------------------------------------------------------------- header

export interface DockSortHeaderProps {
  label: string;
  /** Stated once, here, and never on a cell. */
  unit?: string;
  /** The column's meaning; the sort hint is appended. */
  help?: string;
  /** This column's place in the sort (0 primary, 1 secondary), −1 unsorted. */
  sortAt: number;
  dir: 1 | -1 | null;
  onSort: (shiftKey: boolean) => void;
  align: 'left' | 'center' | 'right';
  /** On the sort button (`sv-th-cut`, `clearance-th-maxIntrusion`). */
  testId?: string;
  /** Shrink the column to its content (`DOCK_TABLE_SHRINK`). */
  shrink?: boolean;
  /** After the button, inside the header cell — an ⓘ that explains it. */
  extra?: ReactNode;
  /** The optional column group this header belongs to (`data-col-group`),
   *  which `useFitColumnGroups` measures. */
  group?: string;
}

/**
 * A sortable column header. ARIA asks for one sorted header at a time, so the
 * secondary sort is announced in text on the button instead of as a second
 * `aria-sort`; every sortable header carries the attribute, so a screen
 * reader is told which columns CAN be sorted. The glyph is drawn by `::after`
 * so the button's text is its label alone.
 */
export const DockSortHeader = ({
  label,
  unit,
  help,
  sortAt,
  dir,
  onSort,
  align,
  testId,
  shrink,
  extra,
  group,
}: DockSortHeaderProps) => {
  const { t } = useTranslation('project');
  const sortHelp = t('stationVolumes.table.sortHelp');
  return (
    <th
      scope='col'
      aria-sort={sortAt === 0 ? (dir === 1 ? 'ascending' : 'descending') : 'none'}
      data-col-group={group}
      className={cn(
        DOCK_TABLE_HEAD,
        align === 'left' ? 'text-left' : align === 'center' ? 'text-center' : 'text-right',
        shrink && DOCK_TABLE_SHRINK,
      )}
    >
      <span className='inline-flex items-center gap-0.5'>
        <button
          type='button'
          data-testid={testId}
          title={help ? `${help} — ${sortHelp}` : sortHelp}
          onClick={(e) => onSort(e.shiftKey)}
          className={cn('inline-flex min-h-6 items-center gap-1 rounded px-0.5', DOCK_CONTROL.focus)}
        >
          <span>{label}</span>
          {unit && <span className='sv-unit font-normal'>{unit}</span>}
          <span
            aria-hidden
            className={cn('dock-sort text-[10px]', sortAt >= 0 ? 'opacity-100' : 'opacity-60')}
            data-glyph={sortAt < 0 ? '↕' : dir === 1 ? '▲' : '▼'}
          />
          {sortAt === 1 && (
            <span className='sr-only'>
              {`${t('stationVolumes.table.sortSecondary')} — ${
                dir === 1 ? t('stationVolumes.table.sortedAscending') : t('stationVolumes.table.sortedDescending')
              }`}
            </span>
          )}
        </button>
        {extra}
      </span>
    </th>
  );
};

/** An em dash that says why, for a cell with no figure. */
export const DockTableNa = ({ text }: { text: string }) => (
  <span title={text}>
    <span aria-hidden>—</span>
    <span className='sr-only'>{text}</span>
  </span>
);

/** The empty-body row ("No intervals match …"). */
export const DockTableEmptyRow = ({
  colSpan,
  testId,
  children,
}: {
  colSpan: number;
  testId?: string;
  children: ReactNode;
}) => (
  <tr>
    <td colSpan={colSpan} data-testid={testId} className={cn('px-2 py-4 text-left', DOCK_TEXT.muted)}>
      {children}
    </td>
  </tr>
);

/** The spacer standing in for the rows above / below the window, so the
 *  scrollbar describes the whole table rather than the rendered slice. */
export const DockTablePad = ({ height, colSpan, testId }: { height: number; colSpan: number; testId?: string }) =>
  height > 0 ? (
    <tr aria-hidden data-testid={testId} style={{ height }}>
      <td colSpan={colSpan} className='p-0' />
    </tr>
  ) : null;

// ----------------------------------------------------------------- window

export interface DockTableWindowOptions<R, K> {
  rows: readonly R[];
  keyOf: (row: R) => K;
  /**
   * The row the rest of the dock is talking about — the walked bin, or the
   * start of a brush. The table scrolls it into view ONLY WHEN THIS VALUE
   * CHANGES (the walk moved from the chart, the 3D bands, the walk toolbar,
   * "Go to worst"). A sort, a filter or a column toggle never chases it
   * (review #5): the reader who sorts Max intrusion descending asked to see
   * the top of the list, not wherever the walked bin now sits.
   */
  revealKey: K | null;
  /** Re-measure the sticky bands when this changes (a column toggle can
   *  rewrap the header; a filter can add a second totals row). */
  measureKey?: unknown;
  /** The viewport ref, when the caller needs it before this hook runs
   *  (`useFitColumnGroups` measures the same element). */
  scrollRef?: RefObject<HTMLDivElement | null>;
}

/**
 * The windowing, reveal, hover and roving-focus machinery both tables share.
 *
 * Also clears the shared hover atom on unmount (review #7): the atom is a bare
 * bin index with no layer key, and a dock closed with the pointer still on a
 * row never fires `pointerleave` — the stale index would then light up that
 * bin in the OTHER dock's chart, the 3D bands and the 2D map.
 */
export function useDockTableWindow<R, K>({
  rows,
  keyOf,
  revealKey,
  measureKey,
  scrollRef: externalScrollRef,
}: DockTableWindowOptions<R, K>) {
  const store = useStore();
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportPx, setViewportPx] = useState(DEFAULT_VIEWPORT_PX);
  /** Height of the in-flow sticky `<thead>`: `scrollTop` 0 is the top of the
   *  TABLE, so every row sits this far further down the scrolled content. */
  const [headPx, setHeadPx] = useState(0);
  /** The row that owns the table's single tab stop, once the arrows moved it. */
  const [focusedKey, setFocusedKey] = useState<K | null>(null);
  const ownScrollRef = useRef<HTMLDivElement | null>(null);
  const scrollRef = externalScrollRef ?? ownScrollRef;
  const bodyRef = useRef<HTMLTableSectionElement | null>(null);
  const hoverRef = useRef<number | null>(null);
  const pendingFocusRef = useRef<K | null>(null);
  const lastRevealRef = useRef<{ key: K | null } | null>(null);
  const lastRowsRef = useRef<readonly R[] | null>(null);

  const visibleCount = Math.max(1, Math.ceil(viewportPx / DOCK_TABLE_ROW_PX));
  const firstRow = Math.max(
    0,
    Math.min(rows.length, Math.floor(Math.max(0, scrollTop - headPx) / DOCK_TABLE_ROW_PX) - OVERSCAN_ROWS),
  );
  const lastRow = Math.min(rows.length, firstRow + visibleCount + 2 * OVERSCAN_ROWS);
  const windowRows = rows.slice(firstRow, lastRow);
  const padTopPx = firstRow * DOCK_TABLE_ROW_PX;
  const padBottomPx = (rows.length - lastRow) * DOCK_TABLE_ROW_PX;

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const measure = () => {
      if (el.clientHeight > 0) setViewportPx(el.clientHeight);
      setHeadPx(el.querySelector<HTMLElement>('thead')?.offsetHeight ?? 0);
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [measureKey]);

  const setScroll = useCallback((next: number) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = next;
    setScrollTop(next);
  }, []);

  /** `scrollIntoView({ block: 'nearest' })` by arithmetic — the row may not be
   *  rendered yet — clear of the sticky header and totals. The bands are read
   *  from the DOM at call time, so a reveal on the first commit (before the
   *  measuring effect's state lands) is already exact. */
  const revealRow = useCallback(
    (position: number) => {
      const el = scrollRef.current;
      if (!el || position < 0) return;
      const headH = el.querySelector<HTMLElement>('thead')?.offsetHeight ?? 0;
      const footH = el.querySelector<HTMLElement>('tfoot')?.offsetHeight ?? 0;
      const next = revealScrollTop({
        current: el.scrollTop,
        viewport: el.clientHeight || DEFAULT_VIEWPORT_PX,
        rowTop: headH + position * DOCK_TABLE_ROW_PX,
        rowH: DOCK_TABLE_ROW_PX,
        headH,
        footH,
      });
      if (next != null) setScroll(next);
    },
    [setScroll],
  );

  // Reveal on a MOVE of the reveal key; back to the top on a new row order
  // (sort, filter, search). Both tracked by the previous value, so neither a
  // re-measure nor an unrelated re-render scrolls anything.
  useEffect(() => {
    const prev = lastRevealRef.current;
    const keyMoved = prev === null || prev.key !== revealKey;
    const rowsChanged = lastRowsRef.current !== null && lastRowsRef.current !== rows;
    lastRevealRef.current = { key: revealKey };
    lastRowsRef.current = rows;
    if (keyMoved && revealKey != null) {
      revealRow(rows.findIndex((r) => keyOf(r) === revealKey));
      return;
    }
    if (rowsChanged && (scrollRef.current?.scrollTop ?? 0) > 0) setScroll(0);
  }, [revealKey, rows, keyOf, revealRow, setScroll]);

  // ---------------------------------------------------------------- hover
  const applyHover = useCallback(() => {
    const body = bodyRef.current;
    if (!body) return;
    body.querySelector('[data-hover="true"]')?.removeAttribute('data-hover');
    const bin = hoverRef.current;
    if (bin == null) return;
    body.querySelector(`[data-bin="${bin}"]`)?.setAttribute('data-hover', 'true');
  }, []);

  useEffect(() => {
    hoverRef.current = store.get(stationVolumeHoverBinAtom);
    applyHover();
    return store.sub(stationVolumeHoverBinAtom, () => {
      hoverRef.current = store.get(stationVolumeHoverBinAtom);
      applyHover();
    });
  }, [store, applyHover]);

  // The window moves rows in and out of the DOM; re-stamp the attribute on
  // whatever is rendered now.
  useEffect(applyHover, [applyHover, firstRow, rows]);

  useEffect(() => () => store.set(stationVolumeHoverBinAtom, null), [store]);

  // A row focused by an arrow key may only exist after the window has moved.
  useEffect(() => {
    const want = pendingFocusRef.current;
    if (want == null) return;
    const el = bodyRef.current?.querySelector(`[data-bin="${String(want)}"]`);
    if (el instanceof HTMLElement) {
      el.focus();
      pendingFocusRef.current = null;
    }
  });

  /** Move the single tab stop one row, revealing (and rendering) it first. */
  const moveFocus = (fromKey: K, step: 1 | -1) => {
    const position = rows.findIndex((r) => keyOf(r) === fromKey);
    const next = rows[position + step];
    if (!next) return;
    const k = keyOf(next);
    setFocusedKey(k);
    pendingFocusRef.current = k;
    revealRow(position + step);
  };

  /** The tab stop: the last row arrowed to, then each fallback in turn (the
   *  walk, the brush), then the top — so tabbing out and back returns you
   *  where you were. */
  const rovingKey = (...fallbacks: (K | null)[]): K | undefined => {
    const has = (k: K | null) => k != null && rows.some((r) => keyOf(r) === k);
    if (has(focusedKey)) return focusedKey as K;
    for (const k of fallbacks) if (has(k)) return k as K;
    return rows[0] === undefined ? undefined : keyOf(rows[0]);
  };

  const onScroll = (e: UIEvent<HTMLDivElement>) => setScrollTop(e.currentTarget.scrollTop);

  return {
    scrollRef,
    bodyRef,
    onScroll,
    windowRows,
    padTopPx,
    padBottomPx,
    setFocusedKey,
    moveFocus,
    rovingKey,
  };
}

// ------------------------------------------------------- width-aware columns

export interface DockColumnGroup {
  /** Matches `data-col-group` on the group's header cells. */
  key: string;
  /** Lower survives longer: the group with the HIGHEST priority hides first. */
  priority: number;
  /** How many columns the group adds — for the "+N columns hidden" hint. */
  columns: number;
  /** Width guess until the group has been on screen once and measured. */
  estPx: number;
  /** The reader's choice from `Columns ▾`; null = automatic. A choice wins
   *  over the width in both directions. */
  explicit: boolean | null;
}

/**
 * Which groups are on, given the room. Explicit choices are honoured first
 * (an explicit ON spends its width even if that overflows — the reader asked
 * for it); the automatic groups then join in priority order while they fit,
 * and stop at the first that does not, so a lower-priority group never shows
 * while a higher one is hidden for want of room.
 */
export function fitColumnGroups(
  available: number,
  base: number,
  groups: readonly DockColumnGroup[],
  widthOf: (key: string) => number,
): Record<string, boolean> {
  const out: Record<string, boolean> = {};
  let used = base;
  for (const g of groups) {
    if (g.explicit != null) {
      out[g.key] = g.explicit;
      if (g.explicit) used += widthOf(g.key);
    }
  }
  let room = true;
  for (const g of [...groups].filter((x) => x.explicit == null).sort((a, b) => a.priority - b.priority)) {
    const w = widthOf(g.key);
    room = room && used + w <= available;
    out[g.key] = room;
    if (room) used += w;
  }
  return out;
}

/**
 * Width-aware optional columns. The table's NATURAL width (its max-content
 * width, read in one synchronous pass with the `w-full` stretch lifted) minus
 * the groups currently shown is the base the groups are fitted against — a
 * figure that does not depend on which groups are on, so hiding one cannot
 * make it fit "again" and flap. Measured widths are cached per group; a group
 * never yet shown uses its `estPx`.
 *
 * Re-fitted when the viewport resizes and after any render that changed the
 * shown set or a choice. With no layout (jsdom, first paint) nothing is hidden.
 */
export function useFitColumnGroups(scrollRef: RefObject<HTMLDivElement | null>, groups: readonly DockColumnGroup[]) {
  const [fitted, setFitted] = useState<Record<string, boolean> | null>(null);
  const widthsRef = useRef<Record<string, number>>({});
  // Explicit choices apply at once, before the next measurement lands.
  const visible: Record<string, boolean> = Object.fromEntries(
    groups.map((g) => [g.key, g.explicit ?? fitted?.[g.key] ?? true]),
  );
  const visibleKey = groups.map((g) => `${g.key}:${visible[g.key] ? 1 : 0}:${g.explicit ?? '-'}`).join('|');
  const groupsRef = useRef(groups);
  const visibleRef = useRef(visible);
  useLayoutEffect(() => {
    groupsRef.current = groups;
    visibleRef.current = visible;
  });

  const refit = useCallback(() => {
    const el = scrollRef.current;
    const table = el?.querySelector('table');
    if (!el || !table || !(el.clientWidth > 0)) return;
    const gs = groupsRef.current;
    const shown = visibleRef.current;
    const prev = table.style.width;
    table.style.width = 'max-content';
    const natural = table.offsetWidth;
    let shownPx = 0;
    for (const g of gs) {
      if (!shown[g.key]) continue;
      let w = 0;
      table.querySelectorAll<HTMLElement>(`thead [data-col-group="${g.key}"]`).forEach((th) => (w += th.offsetWidth));
      if (w > 0) widthsRef.current[g.key] = w;
      shownPx += w;
    }
    table.style.width = prev;
    const next = fitColumnGroups(
      el.clientWidth,
      natural - shownPx,
      gs,
      (k) => widthsRef.current[k] ?? gs.find((g) => g.key === k)!.estPx,
    );
    setFitted((cur) => (cur && gs.every((g) => cur[g.key] === next[g.key]) ? cur : next));
  }, [scrollRef]);

  useLayoutEffect(refit, [refit, visibleKey]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => refit());
    ro.observe(el);
    return () => ro.disconnect();
  }, [scrollRef, refit]);

  const hiddenColumns = groups.reduce((n, g) => n + (g.explicit == null && !visible[g.key] ? g.columns : 0), 0);
  const autoHidden = (key: string) => {
    const g = groups.find((x) => x.key === key);
    return !!g && g.explicit == null && !visible[key];
  };
  return { visible, hiddenColumns, autoHidden };
}
